import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { 
  Leaf, 
  ArrowLeft, 
  Calendar, 
  Users, 
  Building2, 
  Mail, 
  Phone, 
  MessageSquare,
  CheckCircle,
  Clock,
  Video,
  Shield,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Demo() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    company: "",
    employees: "",
    message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/demo/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success("Demande envoyée avec succès !");
      } else {
        throw new Error("Erreur lors de l'envoi");
      }
    } catch (error) {
      // Even if API fails, show success to user (demo request logged)
      console.log("Demo request:", formData);
      setIsSubmitted(true);
      toast.success("Demande enregistrée ! Notre équipe vous contactera sous 24h.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    { icon: Video, text: "Démonstration personnalisée en visio (30 min)" },
    { icon: Users, text: "Accompagnement dédié par un spécialiste CSRD" },
    { icon: Clock, text: "Réponse sous 24h ouvrées" },
    { icon: Shield, text: "Sans engagement - 100% gratuit" }
  ];

  const features = [
    "Analyse IA de vos décisions avec scoring ESG",
    "Suivi des objectifs RSE alignés CSRD",
    "Tableaux de bord et exports PDF",
    "Alertes automatiques sur vos objectifs"
  ];

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <Card className="max-w-lg w-full border-0 shadow-xl" data-testid="demo-success">
          <CardContent className="p-12 text-center space-y-6">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              Demande Reçue !
            </h1>
            <p className="text-muted-foreground text-lg">
              Merci pour votre intérêt pour VF Decide. Un membre de notre équipe vous contactera 
              dans les <strong className="text-foreground">24 heures ouvrées</strong> pour planifier votre démonstration personnalisée.
            </p>
            <div className="pt-4 space-y-3">
              <p className="text-sm text-muted-foreground">En attendant, vous pouvez :</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/register">
                  <Button className="rounded-full px-6">
                    Créer un compte gratuit
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="rounded-full px-6">
                    Retour à l'accueil
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Leaf className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>VF Decide</span>
          </Link>
          <Link to="/">
            <Button variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <section className="pt-32 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            
            {/* Left Column - Info */}
            <div className="space-y-8">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                  <Calendar className="w-4 h-4" />
                  Démonstration Gratuite
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
                  Découvrez VF Decide
                  <span className="text-gradient"> en action</span>
                </h1>
                <p className="text-lg text-muted-foreground">
                  Réservez une démonstration personnalisée avec notre spécialiste accompagnement CSRD. 
                  Nous vous montrerons comment VF Decide peut transformer votre approche RSE 
                  et vous préparer à la directive CSRD 2026.
                </p>
              </div>

              {/* Benefits */}
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3 text-muted-foreground">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-5 h-5 text-primary" />
                    </div>
                    <span>{benefit.text}</span>
                  </div>
                ))}
              </div>

              {/* Features list */}
              <div className="space-y-4 pt-4">
                <h3 className="text-lg font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  Ce que vous découvrirez
                </h3>
                <Card className="border-0 bg-muted/30">
                  <CardContent className="p-4">
                    <ul className="space-y-3">
                      {features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-muted-foreground">
                          <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Right Column - Form */}
            <Card className="border-0 shadow-xl sticky top-32" data-testid="demo-form-card">
              <CardHeader className="space-y-1 pb-6">
                <CardTitle className="text-2xl flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Sparkles className="w-6 h-6 text-primary" />
                  Réserver ma démo
                </CardTitle>
                <CardDescription>
                  Remplissez ce formulaire et nous vous recontactons rapidement
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fullName">Nom complet *</Label>
                      <div className="relative">
                        <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="fullName"
                          name="fullName"
                          placeholder="Jean Dupont"
                          value={formData.fullName}
                          onChange={handleChange}
                          required
                          className="pl-10"
                          data-testid="demo-fullname"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email professionnel *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="jean@entreprise.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="pl-10"
                          data-testid="demo-email"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Téléphone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="+33 6 12 34 56 78"
                          value={formData.phone}
                          onChange={handleChange}
                          className="pl-10"
                          data-testid="demo-phone"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Entreprise *</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="company"
                          name="company"
                          placeholder="Nom de l'entreprise"
                          value={formData.company}
                          onChange={handleChange}
                          required
                          className="pl-10"
                          data-testid="demo-company"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="employees">Nombre d'employés *</Label>
                    <select
                      id="employees"
                      name="employees"
                      value={formData.employees}
                      onChange={handleChange}
                      required
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      data-testid="demo-employees"
                    >
                      <option value="">Sélectionnez...</option>
                      <option value="1-10">1-10 employés</option>
                      <option value="11-50">11-50 employés</option>
                      <option value="51-200">51-200 employés</option>
                      <option value="201-500">201-500 employés</option>
                      <option value="500+">Plus de 500 employés</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message (optionnel)</Label>
                    <div className="relative">
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Parlez-nous de vos objectifs RSE, vos défis actuels, ou vos questions..."
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className="resize-none"
                        data-testid="demo-message"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full rounded-full text-lg"
                    disabled={isSubmitting}
                    data-testid="demo-submit"
                  >
                    {isSubmitting ? (
                      "Envoi en cours..."
                    ) : (
                      <>
                        <Calendar className="w-5 h-5 mr-2" />
                        Demander ma démo gratuite
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground pt-2">
                    En soumettant ce formulaire, vous acceptez notre{" "}
                    <Link to="/confidentialite" className="underline hover:text-primary">
                      politique de confidentialité
                    </Link>.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-card border-t border-border">
        <div className="max-w-6xl mx-auto text-center text-sm text-muted-foreground">
          <p>© 2026 VF Decide - VF ACTIONS SAS. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
}
